import { resolveApiOrigin } from '@vipso/shared';

export type FetchedFileBlob = {
  blob: Blob;
  contentType: string;
  fileName: string;
  fileId?: string;
};

/** Extract numeric file id from `/api/files/:id` (absolute or relative). */
export function parseFileIdFromUrl(url: string): string | null {
  try {
    const path = url.startsWith('http') ? new URL(url).pathname : url;
    const m = path.match(/\/api\/files\/(\d+)\/?$/);
    return m?.[1] ?? null;
  } catch {
    return null;
  }
}

function resolveFetchUrl(input: { fileId?: string | number; url?: string }): string {
  if (input.fileId != null && input.fileId !== '') {
    const origin = resolveApiOrigin().replace(/\/$/, '');
    if (!origin) throw new Error('VITE_API_URL is not set');
    return `${origin}/api/files/${input.fileId}`;
  }
  const raw = String(input.url ?? '').trim();
  if (!raw) throw new Error('fileId or url is required');
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
  if (raw.startsWith('/api/files/')) {
    const origin = resolveApiOrigin().replace(/\/$/, '');
    if (!origin) throw new Error('VITE_API_URL is not set');
    return `${origin}${raw}`;
  }
  throw new Error('Unsupported file URL');
}

/**
 * Fetch a file as a Blob with Bearer auth (preferred for `/api/files/:id`).
 */
export async function fetchFileBlob(
  input: { fileId?: string | number; url?: string; fileName?: string },
  accessToken: string | null | undefined,
): Promise<FetchedFileBlob> {
  const fetchUrl = resolveFetchUrl(input);
  const fileId =
    input.fileId != null && input.fileId !== ''
      ? String(input.fileId)
      : parseFileIdFromUrl(fetchUrl) ?? undefined;

  const headers: Record<string, string> = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const res = await fetch(fetchUrl, { headers });
  if (!res.ok) {
    let message = `Failed to load file (${res.status})`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // ignore
    }
    throw new Error(message);
  }

  const blob = await res.blob();
  const contentType =
    res.headers.get('content-type') || blob.type || 'application/octet-stream';
  const disposition = res.headers.get('content-disposition') || '';
  const matched = disposition.match(/filename="?([^";]+)"?/i);
  const rawName =
    input.fileName?.trim() ||
    matched?.[1] ||
    (fileId ? `file-${fileId}` : 'download');
  // Never label HTML bytes as .pdf — that is what made invoice downloads look "corrupted".
  const fileName = reconcileDownloadFileName(rawName, contentType);

  return { blob, contentType, fileName, fileId };
}

/** Align download extension with Content-Type so OS opens the file correctly. */
export function reconcileDownloadFileName(fileName: string, contentType: string): string {
  const ct = contentType.toLowerCase().split(';')[0]?.trim() ?? '';
  const base = fileName.replace(/\.(pdf|html?|htm)$/i, '') || 'download';
  if (ct.includes('html') || ct === 'text/plain') {
    return `${base}.html`;
  }
  if (ct.includes('pdf')) {
    return `${base}.pdf`;
  }
  if (ct.startsWith('image/')) {
    const subtype = ct.slice('image/'.length).replace(/[^a-z0-9]+/gi, '') || 'img';
    return `${base}.${subtype === 'jpeg' ? 'jpg' : subtype}`;
  }
  return fileName;
}
